import { useReducer, useEffect, MutableRefObject } from 'react';
// 方法
import { IBaseEdiTableContext } from '../context';
import { getUpdateStatus } from '../utils';
// 常量
import { EdiTableDispatchType, IEdiTablePayload } from '../interface';
import {
  STATUS_CREATED,
  STATUS_CREATED_CODE,
  HISTORY_LIST_DATA,
} from '../../constants/constant';
import { FormInstance } from 'antd/lib/form';

export interface IDetailPageAction {
  type: EdiTableDispatchType;
  payload: IEdiTablePayload;
}

// Action
function EdiTableContextAction(
  state: IBaseEdiTableContext,
  action: IDetailPageAction,
): IBaseEdiTableContext {
  const oldDataSource = state.dataSource || [];
  const {
    dataSource,
    addItem,
    index,
    form,
    forms,
    sort,
    startIndex,
    endIndex,
    flagIndex,
    isEmpty,
    updateDataSource,
  } = action.payload;
  switch (action.type) {
    case 'init':
      if (forms && (!dataSource || dataSource.length === 0)) forms.current = [];
      if (
        (!oldDataSource || !oldDataSource.length) &&
        (!dataSource || !dataSource.length)
      ) {
        return {
          ...state,
          forms,
        };
      }
      return {
        ...state,
        forms,
        dataSource: dataSource?.map((item, index) => ({
          ...item,
          key: index,
          sortIndex: index,
          [HISTORY_LIST_DATA]: true,
          [STATUS_CREATED_CODE]:
            item[STATUS_CREATED_CODE] || STATUS_CREATED.DEFAULT,
        })),
      };
    case 'changeDataSource':
      return {
        ...state,
        dataSource: updateDataSource,
      };
    case 'addForm':
      if (!form || index === undefined) return state;
      if (state.forms && state.forms.current) {
        state.forms.current[index] = form;
      }
      return state;
    case 'deleteForm':
      if (index === undefined) return state;
      if (state.forms && state.forms.current) {
        state.forms.current[index] = undefined;
      }
      return state;
    case 'addRow':
      let key = flagIndex || 0;
      const sortIndex = oldDataSource.length || 0;
      let oldDataSourceCopy = oldDataSource;
      if (isEmpty) {
        oldDataSourceCopy = [];
        oldDataSource.forEach(item => {
          if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
            oldDataSourceCopy.push({
              ...item,
              [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
            });
          }
        });
      }
      if (Array.isArray(addItem)) {
        return {
          ...state,
          dataSource: [
            ...oldDataSourceCopy,
            ...addItem.map((v, k) => ({
              [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
              ...v,
              key: key + k,
              sortIndex: sortIndex + k,
            })),
          ],
        };
      }
      return {
        ...state,
        dataSource: [
          ...oldDataSourceCopy,
          {
            [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
            ...addItem,
            key: key,
            sortIndex,
          },
        ],
      };
    case 'moveData':
      if (startIndex === undefined || endIndex === undefined) return state;
      if (startIndex === endIndex) return state;
      let startKey = startIndex;
      let endKey = endIndex;
      const dif = startIndex - endIndex;
      if (dif > 0) {
        startKey = endIndex;
        endKey = startIndex;
      }
      const moveItem = oldDataSource.find((item, k) => k === startIndex);
      const newDataSource = oldDataSource.filter((item, k) => k !== startIndex);
      newDataSource.splice(endIndex, 0, moveItem);
      const nextDataSource = newDataSource.map((item, k) => {
        if (sort && k >= startKey && k <= endKey) {
          return {
            ...item,
            [STATUS_CREATED_CODE]: getUpdateStatus(item[STATUS_CREATED_CODE]),
          };
        }
        return item;
      });
      return {
        ...state,
        dataSource: nextDataSource,
      };
    case 'deleteRow':
      if (index === undefined) return state;
      const deleteData = oldDataSource.find(v => v.key === index);
      if (deleteData) {
        let newDataSource = oldDataSource.filter(v => v.key !== index);
        if (sort) {
          newDataSource = newDataSource.map(item => {
            if (item.key > index) {
              return {
                ...item,
                [STATUS_CREATED_CODE]:
                  item[STATUS_CREATED_CODE] === STATUS_CREATED.DEFAULT
                    ? STATUS_CREATED.UPDATE
                    : item[STATUS_CREATED_CODE],
              };
            }
            return item;
          });
        }
        // 判断当前删除行是否为历史数据
        if (
          deleteData[HISTORY_LIST_DATA] &&
          deleteData[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD
        ) {
          newDataSource.unshift({
            ...deleteData,
            [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
          });
        }
        return {
          ...state,
          dataSource: newDataSource,
        };
      }
      return state;
    default:
      return state;
  }
}

// 初始数据
const initEdiTableContext: IBaseEdiTableContext = {
  editableRef: null,
};

// EdiTableContext
function useEdiTableContext(
  flagIndex: MutableRefObject<number>,
  dataSource?: any[],
  forms?: MutableRefObject<FormInstance[]>,
  sort?: boolean,
) {
  const [EdiTableContext, dispatch] = useReducer(
    EdiTableContextAction,
    initEdiTableContext,
  );
  useEffect(() => {
    flagIndex.current = dataSource?.length || 0;
    dispatch({ type: 'init', payload: { dataSource, forms } });
  }, [dataSource]);
  function dispatchEdiTable(
    type: EdiTableDispatchType,
    payload: IEdiTablePayload = {},
  ) {
    const {
      addItem,
      index,
      form,
      startIndex,
      endIndex,
      isEmpty,
      updateDataSource,
    } = payload;
    switch (type) {
      case 'addRow':
        const num = flagIndex.current;
        flagIndex.current = Array.isArray(addItem)
          ? num + addItem.length
          : num + 1;
        dispatch({ type, payload: { addItem, flagIndex: num, isEmpty } });
        break;
      case 'addForm':
        dispatch({ type, payload: { form, index } });
        break;
      case 'changeDataSource':
        dispatch({ type, payload: { updateDataSource } });
        break;
      case 'deleteForm':
        dispatch({ type, payload: { index } });
        break;
      case 'deleteRow':
        dispatch({ type, payload: { index, sort } });
        break;
      case 'moveData':
        dispatch({ type, payload: { startIndex, endIndex, sort } });
        break;
    }
  }
  return {
    ...EdiTableContext,
    dispatchEdiTable,
  };
}

export default useEdiTableContext;
